import * as React from "react"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import classNames from "classnames"
import { IoSwapHorizontalOutline } from "react-icons/io5"
import { HiOutlineCube } from "react-icons/hi"
import type { GoogleAddressData } from "../place-input"

import { generateFormQueryParams } from "../../services/links"
import { env } from "../../config"
import { useGoogleMaps } from "../../app/hooks/use-google-maps"

import { PlaceInput } from "../place-input"
import { shippingOptions } from "@/constants/form"
import { Select } from "./select"

const addressSchema = z.object({
  formatted_address: z.string(),
  city: z.string(),
  state_code: z.string(),
  lat: z.number(),
  lng: z.number(),
  zip: z.string().nullable(),
})

const transportationSchema = z.object({
  pickup: addressSchema.nullable(),
  delivery: addressSchema.nullable(),
  category: z.string(),
})

export type Props = {
  buttonLink?: string
  buttonLabel?: string
  hasSwapCustomization?: boolean
  geoSessionValue?: string
}

export const PremiumCategoryNavForm: React.FC<Props> = (props) => {
  const {
    buttonLabel,
    buttonLink,
    hasSwapCustomization,
    geoSessionValue,
  } = props
  const APP_HOST = env("APP_HOST")
  const LINK = buttonLink ?? "/shipments/new"
  const getPrefilledPickup = React.useCallback(() => {
    if (!hasSwapCustomization || !geoSessionValue) return null

    try {
      const geoData = JSON.parse(geoSessionValue)
      if (!geoData.country_code2) {
        return null
      }
      return {
        formatted_address: `${geoData.city}, ${geoData.state_prov}, ${geoData.country_name}`,
        city: geoData.city,
        state_code: geoData.state_code,
        lat: parseFloat(geoData.latitude),
        lng: parseFloat(geoData.longitude),
        zip: geoData.zipcode || null,
      } as GoogleAddressData
    } catch (error) {
      console.error("Error parsing geoSessionValue:", error)
      return null
    }
  }, [geoSessionValue, hasSwapCustomization])

  const { handleSubmit, control, getValues, setValue, trigger } = useForm<{
    pickup: null | GoogleAddressData
    delivery: null | GoogleAddressData
    category: string
  }>({
    defaultValues: {
      pickup: getPrefilledPickup(),
      delivery: null,
      category: "animals",
    },
    resolver: zodResolver(transportationSchema),
  })

  React.useEffect(() => {
    const prefilledPickup = getPrefilledPickup()
    if (prefilledPickup) {
      setValue("pickup", prefilledPickup, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      })
    }
  }, [geoSessionValue, setValue, getPrefilledPickup])

  const { mapsLoaded } = useGoogleMaps()

  const handleSwapLocations = async () => {
    const pickupValue = getValues("pickup")
    const deliveryValue = getValues("delivery")

    setValue("pickup", deliveryValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue("delivery", pickupValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })

    await trigger(["pickup", "delivery"])
  }

  const onSubmit = handleSubmit((data) => {
    let queryParams: Record<string, string | number> = {}

    if (data.pickup != null) {
      queryParams.pickupFA = data.pickup.formatted_address
      queryParams.pickupSC = data.pickup.state_code
      queryParams.pickupCt = data.pickup.city
      queryParams.pickupLat = data.pickup.lat
      queryParams.pickupLng = data.pickup.lng
      queryParams.pickupZip = data.pickup.zip ?? ""
    }

    if (data.delivery != null) {
      queryParams.deliveryFA = data.delivery.formatted_address
      queryParams.deliverySC = data.delivery.state_code
      queryParams.deliveryCt = data.delivery.city
      queryParams.deliveryLat = data.delivery.lat
      queryParams.deliveryLng = data.delivery.lng
      queryParams.deliveryZip = data.delivery.zip ?? ""
    }

    const formQueryParams = generateFormQueryParams({
      query: queryParams,
    })
    const route = `${APP_HOST}/shipments/new/${data.category}${formQueryParams}`
    window.location.assign(route)
  })

  return (
    <form
      className="grid grid-cols-1 gap-y-6 sm:flex sm:w-full sm:gap-x-6"
      onSubmit={onSubmit}
    >
      <div className="sm:flex-1">
        <Controller
          control={control}
          name="category"
          render={({ field, fieldState }) => {
            const error = fieldState.error
              ? fieldState.error.message
              : undefined

            return (
              <div>
                <Select
                  value={field.value}
                  ref={field.ref}
                  id="category"
                  onChange={field.onChange}
                  placeholder="What are you shipping?"
                  items={shippingOptions}
                  defaultIcon={
                    <HiOutlineCube size={24} className="w-6 text-gold" />
                  }
                  error={error}
                  isUppercase={false}
                />
              </div>
            )
          }}
        />
      </div>
      <div className="sm:flex-1 -mt-4">
        <Controller
          name="pickup"
          control={control}
          render={({ field, fieldState }) => {
            const error = fieldState.error
              ? fieldState.error.message
              : undefined

            return (
              <PlaceInput
                value={field.value}
                onChange={field.onChange}
                placeholder="Pickup location"
                type="pickup"
                error={error}
                id="pickup-location"
                ref={field.ref}
                showLabel={false}
                mapsLoaded={mapsLoaded}
              />
            )
          }}
        />
      </div>
      {hasSwapCustomization && (
        <div className="flex w-full justify-center rotate-90 md:rotate-0 md:w-10 h-12 flex-shrink-0 items-center self-center md:pt-5 md:mt-4">
          <button
            type="button"
            onClick={handleSwapLocations}
            className={classNames(
              "p-2 rounded-full border border-1 border-gold",
            )}
            aria-label="Swap pickup and delivery locations"
          >
            <IoSwapHorizontalOutline className="w-4 md:w-5" />
          </button>
        </div>
      )}
      <div className="sm:flex-1 -mt-4">
        <Controller
          name="delivery"
          control={control}
          render={({ field, fieldState }) => {
            const error = fieldState.error
              ? fieldState.error.message
              : undefined

            return (
              <PlaceInput
                value={field.value}
                onChange={field.onChange}
                placeholder="Delivery location"
                type="dropoff"
                error={error}
                id="dropoff-location"
                ref={field.ref}
                showLabel={false}
                mapsLoaded={mapsLoaded}
              />
            )
          }}
        />
      </div>

      <div className="sm:flex-1 -mt-4">
        <button
          className="w-full py-4 p-2 rounded-md uppercase text-center bg-blue7 hover:bg-blue-700 mt-4 text-white text-sm lg:text-base"
          onClick={onSubmit}
        >
          {buttonLabel}
        </button>
      </div>
    </form>
  )
}
export default PremiumCategoryNavForm
